import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button } from '../../../../../components/ui/button';
import { Plus, Search, Trash2 } from 'lucide-react';
import { useTeamMembersReduxHook } from '../../../hooks/useTeamMembersRedux';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../../../components/ui/dialog';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { PipeSelector } from '../../../../../components/ui/PipeSelector';
import { MultiSelect, Option } from '../../../../../components/ui/multi-select';
import { Notification as customNotification } from '../../../../../components/ui/custom-notification';
import AddNewItemDialog from '../../../../../components/ui/AddNewItemDialog';
import { SearchableSelect } from '../../../../../components/ui/SearchableSelect';
import { AddMaterialCategory, getListMaterialCategory } from '../../../../../redux/Project/materialSaga';
import { getCertificatePresignedUrl, getPreSignedUrlToUploadCert } from '../../../../../redux/TeamMembers/saga';
import { FileUpload } from '../../../../../components/ui/FileUpload';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { Info } from 'lucide-react';
import { Tooltip } from '../../../../../components/ui/tooltip';
import { AntdDate } from '../../../../../components/ui/antd-date';

interface Document {
  id: string;
  fileName: string;
  uploadDate: string;
}

interface Certification extends Option {
  label: string;
  value: string;
}

interface User {
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  appLevelRole: {
    group_name: string;
  };
}

interface ProjectRole {
  projectLevelRoleId: number;
  projectRoleName: string;
  permissions: Record<string, any>;
  isActive: boolean;
}

interface AddTeamMemberModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onBack?: () => void; // Add back button handler
  selectedTeamMember: any;
  setSelectedTeamMember: (member: any) => void;
  handleTeamMemberDocumentUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSaveTeamMember: () => void;
  refreshTeamMembers: () => void; // Add refresh function
  roles: string[];
  projectRolesData: Array<{projectLevelRoleId: number, projectRoleName: string, isActive: boolean}>;
  certifications: Certification[];
  isAddingRole: boolean;
  setIsAddingRole: (value: boolean) => void;
  isAddingCertification: boolean;
  setIsAddingCertification: (value: boolean) => void;
  newRole: string;
  setNewRole: (value: string) => void;
  newCertification: string;
  setNewCertification: (value: string) => void;
  handleAddRole: () => void;
  handleAddCertification: () => void;
  projectId?: number;
  isLoading?: boolean; // Add loading prop
  onChangesMade?: () => void; // Callback when changes are made
}

interface TeamMemberDocument {
  fileName: string;
  s3Key: string;
  documentTags: string[];
}

interface Qualification {
  qualification_type: string;
  level_type: string;
  certification_number?: string;
  qualification_id?: string;
  expiration_date: string;
}

export const AddTeamMemberModal: React.FC<AddTeamMemberModalProps> = ({
  isOpen,
  onOpenChange,
  onBack,
  selectedTeamMember,
  setSelectedTeamMember,
  handleTeamMemberDocumentUpload,
  handleSaveTeamMember,
  refreshTeamMembers,
  roles,
  projectRolesData,
  certifications,
  isAddingRole,
  setIsAddingRole,
  isAddingCertification,
  setIsAddingCertification,
  newRole,
  setNewRole,
  newCertification,
  setNewCertification,
  handleAddRole,
  handleAddCertification,
  projectId,
  isLoading,
  onChangesMade,
}) => {
  const [isSaving, setIsSaving] = useState(false);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<Array<{
    fileName: string;
    s3Key: string;
    documentTags: string[];
  }>>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessingDocument, setIsProcessingDocument] = useState(false);
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [status, setStatus] = useState<string>('Active');
  const [selectedTags, setSelectedTags] = useState<{ [fileIndex: number]: string[] }>({});
  const [welderId, setWelderId] = useState<string>('');
  
  // Inspection personal details state (for QA/QC role)
  const [certificationNumber, setCertificationNumber] = useState<string>('');
  const [eyesightExaminationPassed, setEyesightExaminationPassed] = useState<boolean | null>(false);
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const [eyesightExaminationExpiryDate, setEyesightExaminationExpiryDate] = useState<string>(getTodayDate());
  const [qualifications, setQualifications] = useState<Qualification[]>([{
    qualification_type: '',
    level_type: '',
    certification_number: '',
    expiration_date: ''
  }]);
  const [qualificationTypes, setQualificationTypes] = useState<any[]>([]);
  const [isAddingQualificationType, setIsAddingQualificationType] = useState(false);
  
  // Permissions state
  type FeatureKey = 'Overview' | 'Setup' | 'Specifications' | 'MaterialTraceability' | 'Registry' | 'OtherDocuments' | 'Pipefitter' | 'Production' | 'TeamMembers' | 'Reports' | 'Analytics';
  type Permission = 'View' | 'Edit' | 'Report' | 'Sign' | 'Manage';

  const FEATURES: FeatureKey[] = ['Overview', 'Setup', 'Specifications', 'MaterialTraceability', 'Pipefitter', 'Production', 'TeamMembers', 'Reports', 'Analytics', 'Registry', 'OtherDocuments'];
  const PERMISSIONS: Permission[] = ['View', 'Edit', 'Report', 'Sign', 'Manage'];
  
  // Default roles list when projectRolesData is empty
  const DEFAULT_ROLES = [
    { projectLevelRoleId: 1, projectRoleName: 'Project Manager' },
    { projectLevelRoleId: 2, projectRoleName: 'Welder' },
    { projectLevelRoleId: 3, projectRoleName: 'QA/QC' },
    { projectLevelRoleId: 4, projectRoleName: 'Customer Representative' },
    { projectLevelRoleId: 5, projectRoleName: 'Material Manager – Coating' },
    { projectLevelRoleId: 6, projectRoleName: 'Material Manager – Pipe' },
    { projectLevelRoleId: 7, projectRoleName: 'Technician' },
    { projectLevelRoleId: 8, projectRoleName: 'QC/QA' },
  ];
  
  const [permissions, setPermissions] = useState<Record<FeatureKey, Record<Permission, boolean>>>(() => {
    const defaultPerms: Record<FeatureKey, Record<Permission, boolean>> = {} as any;
    FEATURES.forEach(feature => {
      defaultPerms[feature] = {
        View: true,
        Edit: false,
        Report: false,
        Sign: false,
        Manage: false,
      };
    });
    return defaultPerms;
  });
  
  // Track if certifications have been processed to prevent duplicate processing
  const certificationsProcessedRef = useRef(false);
  // Track if success notification has been shown to prevent duplicates
  const notificationShownRef = useRef(false);
  // Track if permissions were loaded from API (to prevent overwriting during edit)
  const permissionsLoadedFromApiRef = useRef(false);
  // Ref for file input
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Use default projectId for testing if none provided
  const effectiveProjectId = projectId || 1;

  // Use Redux for users and other operations instead of local state
  const {
    teamMembers,
    loading,
    error,
    deleteLoading,
    addLoading,
    updateLoading,
    projectRoles,
    certifications: reduxCertifications,
    rolesLoading,
    certificationsLoading,
    users,
    usersLoading,
    createUpdateLoading,
    createUpdateTeamMember,
    fetchUsers,
    fetchCertifications
  } = useTeamMembersReduxHook(undefined, effectiveProjectId);

  // Ensure certifications is always an array to prevent errors
  // Use Redux certifications when available, fall back to props
  const safeCertifications = reduxCertifications && reduxCertifications.length > 0 ? reduxCertifications : (certifications || []);
  
  // Convert certifications to tag options format for FileUpload component
  const tagOptions = safeCertifications
    ?.filter((cert: Certification) => cert && cert.value && cert.label)
    .map((cert: Certification) => ({
      value: cert.value,
      label: cert.label
    })) || [];

  // Get list of user IDs already in the project team (excluding current user if editing)
  const existingTeamMemberUserIds = useMemo(() => {
    if (!teamMembers || teamMembers.length === 0) return new Set<number>();
    
    const userIds = new Set<number>();
    teamMembers.forEach((member: any) => {
      // Get user_id from apiData or direct user_id field
      const userId = member.apiData?.user_id || member.user_id;
      if (userId) {
        // Exclude current user if editing (so they can still be selected)
        if (!selectedTeamMember?.id || userId !== (selectedTeamMember.apiData?.user_id || selectedTeamMember.userId)) {
          userIds.add(userId);
        }
      }
    });
    return userIds;
  }, [teamMembers, selectedTeamMember?.id, selectedTeamMember?.apiData?.user_id, selectedTeamMember?.userId]);

  // Filter users to exclude those already in the project team
  const availableUsers = useMemo(() => {
    return users.filter((user: User) => {
      // Filter out Admin and Super users as they already have access
      const groupName = user.appLevelRole?.group_name?.toLowerCase() || '';
      // if (groupName === 'super_admin' || groupName === 'admin') {
      //   return false;
      // }
      // Filter out users already in the project team
      if (existingTeamMemberUserIds.has(user.user_id)) {
        return false;
      }
      return true;
    });
  }, [users, existingTeamMemberUserIds]);
  

  


  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      // Fetch certifications when modal opens
      fetchCertifications({ projectId: effectiveProjectId });
      // Reset notification flag when opening modal
      notificationShownRef.current = false;
      // Reset form state when opening modal
      if (!selectedTeamMember?.id) {
      setSelectedFile(null);
      setFiles([]);
      setSelectedTags({});
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      // Reset status for new team members
      setStatus('Active');
      setWelderId('');
      // Reset inspection personal details for new team members
      setCertificationNumber('');
      setEyesightExaminationPassed(false);
      setEyesightExaminationExpiryDate(getTodayDate());
      setQualifications([{
        qualification_type: '',
        level_type: '',
        certification_number: '',
        expiration_date: ''
      }]);
      //console.log("Permissions:", projectRoles);
      // Reset permissions to defaults
      const defaultPerms: Record<FeatureKey, Record<Permission, boolean>> = {} as any;
      FEATURES.forEach(feature => {
        defaultPerms[feature] = {
          View: true,
          Edit: false,
          Report: false,
          Sign: false,
          Manage: false,
        };
      });
      //  console.log("Default Permissions:", defaultPerms);
      // Try to load saved role access permissions from projectRolesData or projectRoles, fallback to defaults
      const firstProjectRoleData = projectRolesData && projectRolesData.length > 0 ? (projectRolesData[0] as any) : null;
      if (firstProjectRoleData?.projectRolePermissions) {
        const savedPermissions = firstProjectRoleData.projectRolePermissions;
        // Check if it's in feature-based format (has Overview, Setup, etc.)
        if (savedPermissions && typeof savedPermissions === 'object' && !Array.isArray(savedPermissions) &&
            (savedPermissions.Overview || savedPermissions.Setup || savedPermissions.Specifications)) {
          setPermissions(savedPermissions as Record<FeatureKey, Record<Permission, boolean>>);
        } else {
          setPermissions(defaultPerms);
        }
      } else if (projectRoles && projectRoles.length > 0 && projectRoles[0]?.projectRolePermissions) {
        const savedPermissions = projectRoles[0].projectRolePermissions;
        if (savedPermissions && typeof savedPermissions === 'object' && !Array.isArray(savedPermissions) &&
            (savedPermissions.Overview || savedPermissions.Setup || savedPermissions.Specifications)) {
          setPermissions(savedPermissions as Record<FeatureKey, Record<Permission, boolean>>);
        } else {
          setPermissions(defaultPerms);
        }
      } else {
        setPermissions(defaultPerms);
      }
      } else {
        //console.log('selectedTeamMember', selectedTeamMember);
        // If editing, populate user search with existing data
        // Load permissions from apiData.team_member_permissions if available
        if (selectedTeamMember?.apiData?.team_member_permissions) {
          const apiPermissions = selectedTeamMember.apiData.team_member_permissions;
          // Convert API permissions format to component format
          const loadedPermissions: Record<FeatureKey, Record<Permission, boolean>> = {} as any;
          FEATURES.forEach(feature => {
            if (apiPermissions[feature]) {
              loadedPermissions[feature] = {
                View: apiPermissions[feature].View || false,
                Edit: apiPermissions[feature].Edit || false,
                Report: apiPermissions[feature].Report || false,
                Sign: apiPermissions[feature].Sign || false,
                Manage: apiPermissions[feature].Manage || false,
              };
            } else {
              // Default if feature not in API data
              loadedPermissions[feature] = {
                View: true,
                Edit: false,
                Report: false,
                Sign: false,
                Manage: false,
              };
            }
          });
          setPermissions(loadedPermissions);
        }
       
        // Load existing documents if any
        if (selectedTeamMember.documents) {
          setUploadedDocuments(selectedTeamMember.documents.map((doc: any) => ({
            fileName: doc.fileName || doc.file_name,
            s3Key: doc.s3Key || doc.s3_key,
            documentTags: doc.documentTags || doc.document_tags || []
          })));
        }
        
        // Ensure status is set for editing - this will be handled by the mapping effect

      }
    }
  }, [isOpen, selectedTeamMember?.id, fetchUsers, fetchCertifications, effectiveProjectId]);

  // // Load qualification types when modal opens
  // useEffect(() => {
  //   const loadQualificationTypes = async () => {
  //     try {
  //       const tags = await getListMaterialCategory();
  //       if (tags && tags.length > 0) {
  //         const qcQualificationTags = tags.filter((tag: any) => 
  //           tag.tags_type === 'QUALIFICATION_TYPE' ||
  //           tag.tags_type === 'QC_QUALIFICATION_TYPE' ||
  //           tag.tags_type?.toLowerCase().includes('qualification')
  //         );
  //         setQualificationTypes(qcQualificationTags);
  //       }
  //     } catch (error) {
  //       console.error('Failed to load qualification types:', error);
  //     }
  //   };

  //   if (isOpen) {
  //     loadQualificationTypes();
  //   }
  // }, [isOpen]);

  // Load inspection personal details when editing QA/QC team member
  // useEffect(() => {
  //   if (selectedTeamMember?.id && selectedTeamMember?.apiData?.inspection_personal_details) {
  //     const inspectionDetails = typeof selectedTeamMember.apiData.inspection_personal_details === 'string' 
  //       ? JSON.parse(selectedTeamMember.apiData.inspection_personal_details)
  //       : selectedTeamMember.apiData.inspection_personal_details;
      
  //     if (inspectionDetails) {
  //       setCertificationNumber(inspectionDetails.certification_number || '');
  //       setEyesightExaminationPassed(inspectionDetails.eyesight_examination_passed !== undefined 
  //         ? inspectionDetails.eyesight_examination_passed 
  //         : false);
  //       setEyesightExaminationExpiryDate(inspectionDetails.eyesight_examination_expiry_date || getTodayDate());
  //       setQualifications(inspectionDetails.qualifications && inspectionDetails.qualifications.length > 0
  //         ? inspectionDetails.qualifications
  //         : [{
  //             qualification_type: '',
  //             level_type: '',
  //             certification_number: '',
  //             expiration_date: ''
  //           }]);
  //     }
  //   } else if (selectedTeamMember?.role?.toLowerCase() !== 'qa/qc' && selectedTeamMember?.role?.toLowerCase() !== 'qc/qa') {
  //     // Reset inspection details if role is not QA/QC
  //     setCertificationNumber('');
  //     setEyesightExaminationPassed(false);
  //     setEyesightExaminationExpiryDate(getTodayDate());
  //     setQualifications([{
  //       qualification_type: '',
  //       level_type: '',
  //       certification_number: '',
  //       expiration_date: ''
  //     }]);
  //   }
  // }, [selectedTeamMember?.id, selectedTeamMember?.apiData?.inspection_personal_details, selectedTeamMember?.role]);

  // Auto-map API certificates to certifications when editing
  useEffect(() => {
    // Only run this effect when editing (has ID) and has certificates, and certifications haven't been processed yet
    if (selectedTeamMember?.id && selectedTeamMember?.apiData?.certificates && !certificationsProcessedRef.current && safeCertifications && safeCertifications.length > 0) {

      
      // Extract all document tags from API certificates and create a Set for true uniqueness
      const allDocumentTags: string[] = selectedTeamMember.apiData.certificates
        .flatMap((cert: any) => (cert.document_tags || []) as string[])
        .filter((tag: any): tag is string => tag && typeof tag === 'string' && tag.trim() !== ''); // Filter out empty/null tags and ensure string type
      
      // Use Set to ensure true uniqueness and convert back to array
      const uniqueDocumentTags: string[] = Array.from(new Set(allDocumentTags));
      
      // Map document tags to certification objects by searching through available options
      const mappedCertifications = uniqueDocumentTags.map((tag: string) => {
        // First, try to find exact matches (case-insensitive)
        let matchingCert = safeCertifications?.find((cert: Certification) => 
          cert?.label?.toLowerCase() === tag?.toLowerCase() ||
          cert?.value?.toLowerCase() === tag?.toLowerCase()
        );
        
        // If no exact match, try partial matches
        if (!matchingCert) {
          matchingCert = safeCertifications?.find((cert: Certification) => 
            cert?.label?.toLowerCase()?.includes(tag?.toLowerCase()) ||
            tag?.toLowerCase()?.includes(cert?.label?.toLowerCase())
          );
        }
        
        // If still no match, try to find certifications that start with the tag
        if (!matchingCert) {
          matchingCert = safeCertifications?.find((cert: Certification) => 
            cert?.label?.toLowerCase()?.startsWith(tag?.toLowerCase()) ||
            cert?.value?.toLowerCase()?.startsWith(tag?.toLowerCase())
          );
        }
        
        if (matchingCert) {
          return matchingCert;
        } else {
          // Create a new certification object if no match found
          return {
            label: tag,
            value: tag
          };
        }
      });
      
      // Ensure no duplicate certifications by value
      const uniqueMappedCertifications = mappedCertifications.filter((cert: Certification, index: number, arr: Certification[]) => 
        arr.findIndex(c => c?.value?.toLowerCase() === cert?.value?.toLowerCase()) === index
      );
      
      // Update selectedTeamMember with mapped certifications
      setSelectedTeamMember((prev: any) => {
        const updated = {
          ...prev,
          certifications: uniqueMappedCertifications
        };
        return updated;
      });
      
      // Mark certifications as processed
      certificationsProcessedRef.current = true;
    } else if (selectedTeamMember?.id && selectedTeamMember?.apiData?.certificates && certificationsProcessedRef.current) {
      // Certifications already processed, skipping
    }
  }, [selectedTeamMember?.id, selectedTeamMember?.apiData?.certificates, certifications]);


  // Handle user selection
  const handleUserSelect = (user: User) => {
    const fullName = `${user.first_name} ${user.last_name}`.trim();
    
    setSelectedTeamMember({
      ...selectedTeamMember,
      name: fullName,
      email: user.email,
      phone: user.phone_number || '',
      // Don't change the role - keep existing role or leave empty
      role: selectedTeamMember?.role || '',
      userId: user.user_id,
      user_id: user.user_id, // Add user_id for the Select component
    });
  };



  // Direct upload function using team member saga function
  const uploadDocumentDirectly = async (file: File): Promise<{ s3Key: string; fileName: string } | null> => {
    try {

      
      // Get presigned URL using team member saga function
      const presignedData = await getCertificatePresignedUrl({
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size,
        projectId: effectiveProjectId,
        userId: selectedTeamMember?.userId,
      });
      

      
      if (!presignedData?.presignedUrl || !presignedData?.s3Key) {
        throw new Error('Invalid presigned URL response');
      }
      
      const { presignedUrl: uploadUrl, s3Key } = presignedData;
      
      // Upload to S3 using presigned URL

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });
      
      if (!uploadResponse.ok) {
        throw new Error(`S3 upload failed: ${uploadResponse.status}`);
      }
      

      return { s3Key, fileName: file.name };
      
    } catch (error) {
      throw error;
    }
  };

  // Handle document upload with direct upload
  const handleDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !selectedTeamMember?.userId || !effectiveProjectId) {
      customNotification.warning('Missing Information', 'Please select a user and ensure project ID is available before uploading documents.');
      return;
    }

    if (isProcessingDocument) {
      customNotification.warning('Upload in Progress', 'Please wait for the current upload to complete.');
      return;
    }

    setIsUploading(true);
    setIsProcessingDocument(true);
    
    try {
      const newUploadedDocuments: TeamMemberDocument[] = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        

        
        try {
          const uploadResult = await uploadDocumentDirectly(file);
          
          if (uploadResult) {
            const newDocument = {
              fileName: uploadResult.fileName,
              s3Key: uploadResult.s3Key,
              documentTags: [],
            };
            
            newUploadedDocuments.push(newDocument);

          }
        } catch (uploadError) {
          customNotification.error('Upload Failed', `Failed to upload ${file.name}. Please try again.`);
        }
      }
      
      // Update uploaded documents state
      if (newUploadedDocuments.length > 0) {
        setUploadedDocuments(prev => [...prev, ...newUploadedDocuments]);
        // Remove success notification to keep UI simple
      }
      
    } catch (error) {
      customNotification.error('Upload failed', 'Failed to upload documents. Please try again.');
    } finally {
      setIsUploading(false);
      setIsProcessingDocument(false);
    }
  };

  // Handle adding new tag
  const handleAddTag = async (tagName: string) => {
    if (tagName.trim()) {
      if (safeCertifications.find((tag: any) => tag.label === tagName.trim())) {
        customNotification.warning(`${tagName.trim()} certificate already exists`);
        setNewCertification('');
        return;
      }
      try {
        const apiPayload = {
          tags_name: tagName.trim(),
          project_id: effectiveProjectId,
          tags_type: 'Certificate'
        };
        
        const res = await AddMaterialCategory(apiPayload, 'certificate tag');
        if (res) {
          // Refresh certifications after adding new tag
          await fetchCertifications({ projectId: effectiveProjectId });
          // Success message is already shown by AddMaterialCategory
          
          // Auto-select the newly created tag for the first file if it exists
          if (files.length > 0) {
            const newTagValue = tagName.trim();
            setSelectedTags(prev => ({
              ...prev,
              [0]: [...(prev[0] || []), newTagValue]
            }));
          }
          
          // Close the tag dialog
          setIsAddingTag(false);
        }
      } catch (error) {
        customNotification.error('Error', 'Failed to add tag. Please try again.');
      }
    }
  };

  // Handle tags change for FileUpload component
  const handleTagsChange = (fileIndex: number, tags: string[]) => {
    setSelectedTags(prev => ({
      ...prev,
      [fileIndex]: tags
    }));
  };

  // Qualification handlers (for QA/QC role)
  const handleAddQualification = () => {
    setQualifications([...qualifications, {
      qualification_type: '',
      level_type: '',
      certification_number: '',
      expiration_date: ''
    }]);
  };

  const handleRemoveQualification = (index: number) => {
    if (qualifications.length > 1) {
      setQualifications(qualifications.filter((_, i) => i !== index));
    } else {
      customNotification.warning('At least one qualification is required');
    }
  };

  const handleQualificationChange = (index: number, field: keyof Qualification, value: string) => {
    const updated = [...qualifications];
    updated[index] = {
      ...updated[index],
      [field]: value
    };
    setQualifications(updated);
  };

  // Convert qualification types to options for SearchableSelect
  const qualificationTypeOptions = useMemo(() => {
    const defaultOptions = [
      { id: 'welding-inspection', name: 'Welding Inspection' },
      { id: 'aut', name: 'AUT' },
      { id: 'ultrasonic', name: 'Ultrasonic' },
      { id: 'magnetic-particle', name: 'Magnetic Particle' },
      { id: 'radiographic', name: 'Radiographic' },
      { id: 'visual-inspection', name: 'Visual Inspection' },
      { id: 'liquid-penetrant', name: 'Liquid Penetrant' },
      { id: 'eddy-current', name: 'Eddy Current' }
    ];
    const dbOptions = qualificationTypes.map((tag: any) => ({
      id: tag.tags_id?.toString() || '',
      name: tag.tags_name || tag.name || ''
    })).filter(opt => opt.id && opt.name);
    const allOptions = [...dbOptions];
    const dbNames = new Set(dbOptions.map(opt => opt.name.toLowerCase()));
    defaultOptions.forEach(defaultOpt => {
      if (!dbNames.has(defaultOpt.name.toLowerCase())) {
        allOptions.push(defaultOpt);
      }
    });
    return allOptions.length > 0 ? allOptions : defaultOptions;
  }, [qualificationTypes]);

  const handleAddQualificationType = async (typeName: string) => {
    if (typeName.trim()) {
      // Check if it already exists in current options
      if (qualificationTypeOptions.find((opt: any) => opt.name.toLowerCase() === typeName.trim().toLowerCase())) {
        customNotification.warning(`${typeName.trim()} qualification type already exists`);
        return;
      }

      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const apiPayload = {
        tags_name: typeName.trim(),
        project_id: effectiveProjectId,
        tags_type: 'QUALIFICATION_TYPE',
        created_by: user.email || ''
      };
      const res = await AddMaterialCategory(apiPayload, 'Qualification Type');

      if (res) {
        // Reload qualification types from database
        try {
          const tags = await getListMaterialCategory();
          if (tags && tags.length > 0) {
            const qcQualificationTags = tags.filter((tag: any) => 
              tag.tags_type === 'QUALIFICATION_TYPE' ||
              tag.tags_type === 'QC_QUALIFICATION_TYPE' ||
              tag.tags_type?.toLowerCase().includes('qualification')
            );
            setQualificationTypes(qcQualificationTags);
          }
        } catch (error) {
          console.error('Failed to reload qualification types:', error);
          const updatedTypes = [...qualificationTypes, res];
          setQualificationTypes(updatedTypes);
        }
        setIsAddingQualificationType(false);
      }
    }
  };

  // Handle file select for FileUpload component
  const handleFileSelect = (selectedFiles: FileList | null) => {
    if (selectedFiles && selectedFiles.length > 0) {
      const fileArray = Array.from(selectedFiles);
      const updatedFiles = [...files, ...fileArray];
      setFiles(updatedFiles);
      setSelectedFile(fileArray[0]); // Keep for backward compatibility
    }
  };

  // Handle remove file
  const handleRemoveFile = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    
    // Update selectedTags to remove tags for removed file
    const updatedTags: { [fileIndex: number]: string[] } = {};
    updatedFiles.forEach((_, i) => {
      if (i < index) {
        updatedTags[i] = selectedTags[i] || [];
      } else if (i > index) {
        updatedTags[i - 1] = selectedTags[i] || [];
      }
    });
    setSelectedTags(updatedTags);
    
    // Update selectedFile if it was the removed file
    if (index === 0 && updatedFiles.length > 0) {
      setSelectedFile(updatedFiles[0]);
    } else if (updatedFiles.length === 0) {
      setSelectedFile(null);
    }
  };

  // Handle permissions toggle
  const togglePermission = (feature: FeatureKey, permission: Permission) => {
    setPermissions(prev => ({
      ...prev,
      [feature]: {
        ...prev[feature],
        [permission]: !prev[feature][permission],
      },
    }));
  };

  // Set all permissions for a feature
  const setAllPermissionsForFeature = (feature: FeatureKey, value: boolean) => {
    setPermissions(prev => ({
      ...prev,
      [feature]: PERMISSIONS.reduce((acc, perm) => ({ ...acc, [perm]: value }), {} as Record<Permission, boolean>),
    }));
  };

  // Set permission for all features
  const setPermissionForAllFeatures = (permission: Permission, value: boolean) => {
    setPermissions(prev => {
      const updated = { ...prev };
      FEATURES.forEach(feature => {
        updated[feature] = { ...updated[feature], [permission]: value };
      });
      return updated;
    });
  };

  // Get permission help text
  const getPermissionHelp = (permission: Permission): string => {
    const help: Record<Permission, string> = {
      View: 'Read-only access to data in this feature.',
      Edit: 'Create/update/delete entries in this feature.',
      Report: 'Generate/export reports (PDF/CSV) for this feature.',
      Sign: 'Digitally sign or approve records/reports.',
      Manage: 'Invite users and adjust access settings for this feature.',
    };
    return help[permission];
  };

  // Get feature display name
  const getFeatureDisplayName = (feature: FeatureKey): string => {
    const names: Record<FeatureKey, string> = {
      Overview: 'Overview',
      Setup: 'Setup',
      Specifications: 'Specifications',
      MaterialTraceability: 'Material Traceability',
      Pipefitter: 'Pipefitter',
      Production: 'Production',
      TeamMembers: 'Team Members',
      Reports: 'Reports',
      Analytics: 'Analytics',
      Registry: 'Registry',
      OtherDocuments: 'Other Documents',
    };
    return names[feature];
  };

  // Check if all features have a permission
  const allFeaturesHavePermission = (permission: Permission): boolean => {
    return FEATURES.every(feature => permissions[feature][permission]);
  };

  // Check if any feature has a permission
  const anyFeatureHasPermission = (permission: Permission): boolean => {
    return FEATURES.some(feature => permissions[feature][permission]);
  };

  // Update permissions based on selected role from saved project level role access
  const updatePermissionsBasedOnRole = useCallback((roleName: string) => {
    if (!roleName) {
      // Reset to defaults if no role selected
      const defaultPerms: Record<FeatureKey, Record<Permission, boolean>> = {} as any;
      FEATURES.forEach(feature => {
        defaultPerms[feature] = {
          View: true,
          Edit: false,
          Report: false,
          Sign: false,
          Manage: false,
        };
      });
      setPermissions(defaultPerms);
      permissionsLoadedFromApiRef.current = false;
      return;
    }

    // Try to find the role in saved project level role access (from projectRoles Redux)
    if (projectRoles && Array.isArray(projectRoles) && projectRoles.length > 0) {
      const firstEntry = projectRoles[0];
      const rolesArray = firstEntry?.projectRolePermissions?.roles || [];
      
      // Find the role by matching role name
      const roleData = rolesArray.find((role: any) => 
        (role.projectRoleName || role.project_role_name) === roleName
      );

      if (roleData && roleData.permissions) {
        // Map API permissions format to feature-based permissions
        const apiPermissions = roleData.permissions;
        let updatedPermissions: Record<FeatureKey, Record<Permission, boolean>> = {} as any;
        
        // Check if permissions are in feature-based format
        if (apiPermissions.Overview || apiPermissions.Setup || apiPermissions.Specifications) {
          // Feature-based format: { Overview: { View: true, Edit: false, ... }, ... }
          FEATURES.forEach(feature => {
            if (apiPermissions[feature]) {
              updatedPermissions[feature] = {
                View: apiPermissions[feature].View || false,
                Edit: apiPermissions[feature].Edit || false,
                Report: apiPermissions[feature].Report || false,
                Sign: apiPermissions[feature].Sign || false,
                Manage: apiPermissions[feature].Manage || false,
              };
            } else {
              // Default if feature not in API data
              updatedPermissions[feature] = {
                View: true,
                Edit: false,
                Report: false,
                Sign: false,
                Manage: false,
              };
            }
          });
        } else if (apiPermissions.project && Array.isArray(apiPermissions.project)) {
          // Old format: project array like ['read', 'write', 'report', 'sign', 'manage']
          // Apply these permissions to all features
          const projectPerms = apiPermissions.project;
          const hasRead = projectPerms.some((p: string) => 
            p.toLowerCase() === 'read' || p.toLowerCase() === 'view'
          );
          const hasWrite = projectPerms.some((p: string) => 
            p.toLowerCase() === 'write' || p.toLowerCase() === 'edit'
          );
          const hasReport = projectPerms.some((p: string) => 
            p.toLowerCase() === 'report'
          );
          const hasSign = projectPerms.some((p: string) => 
            p.toLowerCase() === 'sign'
          );
          const hasManage = projectPerms.some((p: string) => 
            p.toLowerCase() === 'manage'
          );

          // Apply these permissions to all features
          FEATURES.forEach(feature => {
            updatedPermissions[feature] = {
              View: hasRead,
              Edit: hasWrite,
              Report: hasReport,
              Sign: hasSign,
              Manage: hasManage,
            };
          });
        } else {
          // Unknown format, use defaults
          FEATURES.forEach(feature => {
            updatedPermissions[feature] = {
              View: true,
              Edit: false,
              Report: false,
              Sign: false,
              Manage: false,
            };
          });
        }

        setPermissions(updatedPermissions);
        permissionsLoadedFromApiRef.current = true; // Mark as loaded from API
        return;
      }
    }

    // Fallback to defaults if role not found in saved project level role access
    permissionsLoadedFromApiRef.current = false;
    
    // Use default permissions when role not found
    const defaultPerms: Record<FeatureKey, Record<Permission, boolean>> = {} as any;
    FEATURES.forEach(feature => {
      defaultPerms[feature] = {
        View: true,
        Edit: false,
        Report: false,
        Sign: false,
        Manage: false,
      };
    });
    setPermissions(defaultPerms);
  }, [projectRoles]);

  // Handle save with loading state
  const handleSave = async () => {  
    
    // In edit mode, we don't need userId since the team member already exists
    if (!selectedTeamMember?.id && !selectedTeamMember?.userId) {
      customNotification.warning('User selection required', 'Please select a user first to continue.');
      return;
    }

    if (!selectedTeamMember?.role) {
      customNotification.warning('Role selection required', 'Please select a role first to continue.');
      return;
    }

    if (!status) {
      customNotification.warning('Status selection required', 'Please select a status first to continue.');
      return;
    }

    // Validate welderId if role is Welder - check early before setting saving state
    if (selectedTeamMember.role?.toLowerCase() === 'welder' && !welderId?.trim()) {
      customNotification.warning('Welder ID required', 'Please enter a welder ID for the welder role.');
      return;
    }

    if (!effectiveProjectId) {
      customNotification.error('Configuration error', 'Project ID is missing. Please check the component props.');
      return;
    }



    setIsSaving(true);
    // Reset notification flag when starting new operation
    notificationShownRef.current = false;
    
    // Notify parent that changes are being made
    if (onChangesMade) {
      onChangesMade();
    }
    
    try {
      let uploadedDocumentData:any = [];
      
      // If there are files, upload them first
      if (files.length > 0) {
        setIsUploading(true);
        setIsProcessingDocument(true);
        
        try {
          const uploadResults = await getPreSignedUrlToUploadCert(files, {
            projectId: effectiveProjectId,
            userId: selectedTeamMember?.userId,
          });
          if (!uploadResults || uploadResults.length === 0) {
            throw new Error('Failed to get presigned URLs');
          }
          uploadedDocumentData = uploadResults.map((result, index) => ({
            fileName: result.fileName,
            s3Key: result.s3Key,
            documentTags: selectedTags[index] || [],
          }));
        } catch (uploadError) {
          customNotification.error('Upload Failed', `Failed to upload files. Please try again.`);
          setIsSaving(false);
          setIsUploading(false);
          setIsProcessingDocument(false);
          return;
        } finally {
          setIsUploading(false);
          setIsProcessingDocument(false);
        }
      }

      // Find the selected project role ID from the projectRolesData or DEFAULT_ROLES
      const rolesToSearch = (projectRolesData && projectRolesData.length > 0) ? projectRolesData : DEFAULT_ROLES;
      const selectedRoleData = rolesToSearch.find(role => role.projectRoleName === selectedTeamMember.role);
      if (!selectedRoleData) {
        customNotification.error('Role not found', 'Selected role not found. Please try selecting a different role.');
        setIsSaving(false);
        return;
      }
      
      // Validate required fields
      if (!selectedTeamMember.role || !selectedRoleData.projectRoleName) {
        customNotification.error('Validation Error', 'Team member role is missing. Please select a role.');
        setIsSaving(false);
        return;
      }

      if (!effectiveProjectId) {
        customNotification.error('Validation Error', 'Project ID is missing. Please check the component configuration.');
        setIsSaving(false);
        return;
      }

      if (!selectedTeamMember?.userId && !selectedTeamMember?.apiData?.user_id) {
        customNotification.error('Validation Error', 'User ID is missing. Please select a user first.');
        setIsSaving(false);
        return;
      }

      if (!status) {
        customNotification.warning('Status selection required', 'Please select a status first to continue.');
        setIsSaving(false);
        return;
      }
      const isWelder = selectedTeamMember.role?.toLowerCase() === 'welder';
      const welderFormattedId = isWelder ? `CRCE_WELDER;I;${welderId?.trim()}` : '';

      // Prepare inspection personal details for QA/QC role
      const isQAQC =selectedTeamMember?.role === 'QA/QC' || selectedTeamMember?.role?.toLowerCase() === 'QC/QA';
      const inspectionPersonalDetails = isQAQC ? {
        certification_number: certificationNumber?.trim() || null,
        eyesight_examination_passed: eyesightExaminationPassed,
        eyesight_examination_expiry_date: eyesightExaminationExpiryDate || null,
        qualifications: qualifications
          .filter(q => q.qualification_type?.trim()) // Only include qualifications with type
          .map(q => ({
            qualification_type: q.qualification_type?.trim() || null,
            level_type: q.level_type?.trim() || null,
            certification_number: q.certification_number?.trim() || null,
            qualification_id: q.qualification_id?.trim() || null,
            expiration_date: q.expiration_date || null
          }))
      } : null;

      // Prepare team member data
      const teamMemberData: any = {
        data: {
          team_member_id: selectedTeamMember?.id ? parseInt(selectedTeamMember.id) : undefined, // Convert string ID to number for updates
          user_id: selectedTeamMember?.id ? selectedTeamMember.apiData?.user_id : selectedTeamMember.userId, // Use existing user_id for edits
          user_name: selectedTeamMember?.apiData?.user ? `${selectedTeamMember.apiData.user.first_name} ${selectedTeamMember.apiData.user.last_name}`.trim() : selectedTeamMember.name || selectedTeamMember?.apiData?.user?.first_name + ' ' + selectedTeamMember?.apiData?.user?.last_name,
          project_id: effectiveProjectId,
          team_member_role: selectedTeamMember.role, // Use the role name (string) instead of ID
          team_member_permissions: permissions,
          status: status, // Use the status state
          certificates: selectedTeamMember?.id 
            ? selectedTeamMember.certifications
            : uploadedDocumentData.length > 0 
              ? uploadedDocumentData.map((doc: any) => ({
                  file_name: doc.fileName,
                  document_tags: doc.documentTags,
                  s3Key: doc.s3Key
                } as any))
              : []
        }
      };

      // Only add Welder fields if role is Welder
      if (isWelder) {
        teamMemberData.data.Welder_Id = welderId.trim();
        teamMemberData.data.welder_qr_code = welderFormattedId;
      }

      // Only add inspection personal details if role is QA/QC
      if (isQAQC && inspectionPersonalDetails) {
        teamMemberData.data.inspection_personal_details = (inspectionPersonalDetails);
      }

      // Log the status being sent


    
  // console.log("teamMemberData", teamMemberData);

      // Call the API to save the team member
      // Don't await here - let the effect handle completion based on createUpdateLoading
      createUpdateTeamMember(teamMemberData);
      
      // Keep isSaving true - will be reset by the effect when createUpdateLoading becomes false
      // Success notification is handled by the saga
      
    } catch (error: any) {
      
      // Operation failed
      const errorMessage = typeof error === 'string' ? error : (error?.message || 'An unknown error occurred');
      customNotification.error('Failed to save team member', errorMessage);
      setIsSaving(false);
      setIsUploading(false);
      setIsProcessingDocument(false);
    }
  };

  // Reset certifications processed flag when modal opens or team member changes
  useEffect(() => {
    if (isOpen && selectedTeamMember?.id) {
      // Reset the flags when editing a team member
      certificationsProcessedRef.current = false;
      permissionsLoadedFromApiRef.current = false;
    } else if (isOpen && !selectedTeamMember?.id) {
      // Reset the flags when adding a new team member
      certificationsProcessedRef.current = false;
      permissionsLoadedFromApiRef.current = false;
    }
  }, [isOpen, selectedTeamMember?.id]);

  // Reset form inputs when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Reset all form inputs when modal closes

      setSelectedFile(null);
      setFiles([]);
      setSelectedTags({});
      setUploadedDocuments([]);
      setStatus('Active');
      setWelderId('');
      setIsAddingTag(false);
      setIsAddingRole(false);
      setIsAddingCertification(false);
      // Reset inspection personal details
      setCertificationNumber('');
      setEyesightExaminationPassed(false);
      setEyesightExaminationExpiryDate(getTodayDate());
      setQualifications([{
        qualification_type: '',
        level_type: '',
        certification_number: '',
        expiration_date: ''
      }]);
      setIsAddingQualificationType(false);
      // Reset permissions to defaults
      const defaultPerms: Record<FeatureKey, Record<Permission, boolean>> = {} as any;
      FEATURES.forEach(feature => {
        defaultPerms[feature] = {
          View: true,
          Edit: false,
          Report: false,
          Sign: false,
          Manage: false,
        };
      });
      setPermissions(defaultPerms);
      
      // Reset new role and certification inputs
      setNewRole('');
      setNewCertification('');
      
      // Reset file input if it exists
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Reset certifications processed flag
      certificationsProcessedRef.current = false;
      
      // Reset notification flag
      notificationShownRef.current = false;
      
      // Reset selected team member if not editing
      if (!selectedTeamMember?.id) {
        setSelectedTeamMember({
          id: '',
          name: '',
          email: '',
          phone: '',
          role: '',
          certifications: [],
          qualifications: [],
          documents: [],
          status: 'active'
        });
      }
    }
  }, [isOpen, selectedTeamMember?.id, setNewRole, setNewCertification]);

  // Additional effect to track loading state transitions and handle save completion
  useEffect(() => {
    if (createUpdateLoading === true) {
      // Reset notification flag when loading starts
      notificationShownRef.current = false;
    } else if (createUpdateLoading === false && isSaving) {
      // Save operation completed (either success or failure)
      // Check if there was an error
      if (error && error.type === 'CREATE_UPDATE_ERROR') {
        // Error occurred - reset saving state but keep modal open
        setIsSaving(false);
        setIsUploading(false);
        setIsProcessingDocument(false);
        // Error notification is already shown by the saga
      } else {
        // Success - close the modal and reset form state
        onOpenChange(false);
        
        // Reset form state
        setSelectedTeamMember(null);

        setSelectedFile(null);
        setFiles([]);
        setSelectedTags({});
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        // Reset saving state
        setIsSaving(false);
        setIsUploading(false);
        setIsProcessingDocument(false);
      }
    }
  }, [createUpdateLoading, isSaving, error, onOpenChange, setSelectedTeamMember]);

  // Map API data to component fields when editing
  useEffect(() => {
    if (selectedTeamMember?.id && selectedTeamMember?.apiData) {

      
      // When editing, ensure all required fields are mapped from apiData
      setSelectedTeamMember((prev: any) => {
        const updated = {
          ...prev,
          userId: selectedTeamMember.apiData.user_id,
          name: selectedTeamMember.apiData.user?.first_name + ' ' + selectedTeamMember.apiData.user?.last_name,
          email: selectedTeamMember.apiData.user?.email,
          phone: selectedTeamMember.apiData.user?.phone_number || '',
          role: selectedTeamMember.apiData.team_member_role || prev.role
        };
        

        return updated;
      });
      
      // Set status from API data when editing - handle case sensitivity
      if (selectedTeamMember.apiData.status) {
        const apiStatus = selectedTeamMember.apiData.status;

        
        // Normalize status to match dropdown options
        let normalizedStatus = 'Active'; // default
        if (apiStatus.toLowerCase() === 'active' || apiStatus.toLowerCase() === 'act') {
          normalizedStatus = 'Active';
        } else if (apiStatus.toLowerCase() === 'inactive' || apiStatus.toLowerCase() === 'inact') {
          normalizedStatus = 'Inactive';
        }
        

        setStatus(normalizedStatus);
      } else {

        setStatus('Active');
      }
      
      // Load welderId from API data if available
      if (selectedTeamMember.apiData.welderId || selectedTeamMember.apiData.welder_id) {
        setWelderId(selectedTeamMember.apiData.welderId || selectedTeamMember.apiData.welder_id || '');
      } else {
        setWelderId('');
      }
    }
  }, [selectedTeamMember?.id, selectedTeamMember?.apiData]);

  // Load permissions from API when editing
  useEffect(() => {
    if (selectedTeamMember?.id && selectedTeamMember?.apiData?.team_member_permissions && !permissionsLoadedFromApiRef.current) {
      const apiPermissions = selectedTeamMember.apiData.team_member_permissions;
      const loadedPermissions: Record<FeatureKey, Record<Permission, boolean>> = {} as any;
      FEATURES.forEach(feature => {
        if (apiPermissions[feature]) {
          loadedPermissions[feature] = {
            View: apiPermissions[feature].View || false,
            Edit: apiPermissions[feature].Edit || false,
            Report: apiPermissions[feature].Report || false,
            Sign: apiPermissions[feature].Sign || false,
            Manage: apiPermissions[feature].Manage || false,
          };
        } else {
          loadedPermissions[feature] = {
            View: true,
            Edit: false,
            Report: false,
            Sign: false,
            Manage: false,
          };
        }
      });
      setPermissions(loadedPermissions);
      permissionsLoadedFromApiRef.current = true;
    } else if (!selectedTeamMember?.id) {
      // Reset flag when adding new member
      permissionsLoadedFromApiRef.current = false;
    }
  }, [selectedTeamMember?.id, selectedTeamMember?.apiData?.team_member_permissions]);

  // Update permissions when role changes (for both new and existing team members)
  // But only if permissions weren't manually loaded from API or if user explicitly changes role
  useEffect(() => {
    if (selectedTeamMember?.role) {
      // For new members, always update permissions based on role
      if (!selectedTeamMember?.id) {
        updatePermissionsBasedOnRole(selectedTeamMember.role);
      } 
      // For existing members, update permissions when role changes (user changed the role)
      else if (selectedTeamMember?.apiData?.team_member_role !== selectedTeamMember.role) {
        // Role has changed from the original, update permissions based on new role
        // updatePermissionsBasedOnRole will set permissionsLoadedFromApiRef based on whether it found the role in API
        updatePermissionsBasedOnRole(selectedTeamMember.role);
      }
    }
  }, [selectedTeamMember?.role, selectedTeamMember?.id, selectedTeamMember?.apiData?.team_member_role, updatePermissionsBasedOnRole]);

  // Monitor status state changes for debugging
  useEffect(() => {

  }, [status]);

  // Ensure status is properly set when editing - fallback effect
  useEffect(() => {
    if (isOpen && selectedTeamMember?.id && selectedTeamMember?.apiData?.status && !status) {

      const apiStatus = selectedTeamMember.apiData.status;
      
      // Normalize status to match dropdown options
      let normalizedStatus = 'Active'; // default
      if (apiStatus.toLowerCase() === 'active' || apiStatus.toLowerCase() === 'act') {
        normalizedStatus = 'Active';
      } else if (apiStatus.toLowerCase() === 'inactive' || apiStatus.toLowerCase() === 'inact') {
        normalizedStatus = 'Inactive';
      }
      

      setStatus(normalizedStatus);
    }
  }, [isOpen, selectedTeamMember?.id, selectedTeamMember?.apiData?.status, status]);

  // Handle back button
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      onOpenChange(false);
    }
  };

  if (!isOpen) {
    return (
      <Dialog open={false} onOpenChange={onOpenChange}>
        <DialogTrigger asChild>
          <Button 
            size="sm"
            className="bg-[#EB1C37] text-white hover:bg-[#EB1C37]/90 flex items-center gap-2 min-w-[140px] justify-center"
          >
            <Plus className="h-4 w-4" />
            Add Member
          </Button>
        </DialogTrigger>
      </Dialog>
    );
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between gap-3 mb-6">
        <h2 className="text-[18px] font-semibold text-blueprint-blue leading-tight">
          {selectedTeamMember?.id ? 'Edit Team Member' : 'Add New Team Member'}
        </h2>
        <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleBack}
              className="h-9 px-4 border-[#E2E5EA] text-text-grey hover:bg-page-bg"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              size="sm"
              disabled={
                !selectedTeamMember?.role ||
                !selectedTeamMember?.userId ||
                isSaving || isLoading || createUpdateLoading
              }
              className="h-9 px-4 bg-hero-red text-white hover:bg-hero-red-60"
            >
              {(isSaving || isLoading || createUpdateLoading) ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        <div className="space-y-6 relative">

          


          <div className={`grid grid-cols-${selectedTeamMember?.role?.toLowerCase() === 'welder' ? 3 : 2} gap-6`}>
            <div>
              <Label htmlFor="name" className="text-[12px] font-medium text-text-grey">Name <span className="text-hero-red">*</span></Label>
              <div className="relative mt-1">
                {selectedTeamMember?.id ? (
                  // Edit mode: Show disabled name field with current user info
                  <Input
                    id="name"
                    value={selectedTeamMember?.name || ''}
                    className="bg-page-bg text-text-grey cursor-not-allowed"
                    disabled={true}
                  />
                ) : (
                  // Add mode: Show user selection dropdown
                  <SearchableSelect
                    value={selectedTeamMember?.name || ''}
                    onValueChange={(value: string) => {
                      // value is the full name from SearchableSelect
                      const selectedUser = availableUsers.find((user: User) => {
                        const fullName = `${user.first_name} ${user.last_name}`.trim();
                        return fullName === value;
                      });
                      if (selectedUser) {
                        handleUserSelect(selectedUser);
                      }
                    }}
                    placeholder="Select user..."
                    searchPlaceholder="Search users..."
                    options={availableUsers.map((user: User) => {
                      const fullName = `${user.first_name} ${user.last_name}`.trim();
                      return {
                        id: user.user_id,
                        name: fullName
                      };
                    })}
                    showAddButton={false}
                    disabled={usersLoading}
                    loading={usersLoading}
                    loadingText="Loading users..."
                  />
                )}
              </div>
              {selectedTeamMember?.email && (
                <p className="text-[11px] text-heritage-grey mt-1">
                  Email: {selectedTeamMember.email}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="role" className="text-[12px] font-medium text-text-grey">Role <span className="text-hero-red">*</span></Label>
              <div className="relative mt-1">
                <SearchableSelect
                  value={selectedTeamMember?.role || ''}
                  onValueChange={(value: string) => {
                    if (selectedTeamMember) {
                      const updated = {
                        ...selectedTeamMember,
                        role: value,
                      };

                      setSelectedTeamMember(updated);
                      
                      // Update permissions based on selected role from saved project level role access
                      // This will load permissions from projectRoles if available
                      updatePermissionsBasedOnRole(value);
                      
                      // Reset welderId if role is changed away from Welder
                      if (value?.toLowerCase() !== 'welder') {
                        setWelderId('');
                      }
                    }
                  }}
                  placeholder="Select role"
                  searchPlaceholder="Search roles..."
                  options={(projectRolesData && projectRolesData.length > 0 
                    ? projectRolesData 
                    : DEFAULT_ROLES
                  ).map((role) => ({
                    id: role.projectLevelRoleId,
                    name: role.projectRoleName
                  }))}
                  showAddButton={false}
                  disabled={rolesLoading}
                  loading={rolesLoading}
                  loadingText="Loading roles..."
                  
                />
              </div>
            </div>
             {/* Welder ID field - only show when role is Welder */}
          {selectedTeamMember?.role?.toLowerCase() === 'welder' && (
            <div>
              <Label htmlFor="welderId" className="text-[12px] font-medium text-text-grey">Welder ID <span className="text-hero-red">*</span></Label>
              <div className="relative mt-1">
                <Input
                  id="welderId"
                  value={welderId}
                  onChange={(e) => setWelderId(e.target.value)}
                  placeholder="Enter welder ID"
                />
              </div>
            </div>
          )}
          </div>
         
          {selectedTeamMember?.id && (
            <div className="grid grid-cols-2 gap-6">
              <div>
                <Label htmlFor="status" className="text-[12px] font-medium text-text-grey">Status</Label>
                <div className="relative mt-1">
                  <PipeSelector
                    value={status || null}
                    onChange={(v) => setStatus(v)}
                    placeholder="Select status"
                    options={[
                      { value: 'Active', label: 'Active' },
                      { value: 'Inactive', label: 'Inactive' },
                    ]}
                    triggerWidth="w-full"
                  />
                </div>
              </div>
            </div>
          )}
          {/* <div>
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              placeholder="Phone will be auto-populated"
              value={selectedTeamMember?.phone || ''}
              disabled={true}
              className="bg-gray-50 text-gray-600 cursor-not-allowed"
            />
          </div> */}

          {/* Inspection Personal Details Section - Only show when QA/QC role is selected */}
          {/* TODO: Enable this feature when ready - set to false to hide */}
          {false && (selectedTeamMember?.role?.toLowerCase() === 'qa/qc' || selectedTeamMember?.role?.toLowerCase() === 'qc/qa') && (
            <div className="space-y-6 border-t pt-6">
              <h3 className="text-lg font-semibold">Inspection Personal Details</h3>
              
              <div className="grid grid-cols-2 gap-6">
                {/* Eyesight Certification Number */}
                <div className="space-y-2">
                  <Label htmlFor="certificationNumber">Certification Number</Label>
                  <Input
                    id="certificationNumber"
                    value={certificationNumber}
                    onChange={(e) => setCertificationNumber(e.target.value)}
                    placeholder="Enter certification number"
                    className="w-full"
                  />
                </div>

                {/* Eyesight Examination Passed - Yes/No Toggle */}
                <div className="space-y-2">
                  <Label>Eyesight Examination Passed</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Button
                      type="button"
                      variant={eyesightExaminationPassed === true ? "default" : "outline"}
                      onClick={() => setEyesightExaminationPassed(true)}
                      className={eyesightExaminationPassed === true ? "bg-[#EB1C37] text-white hover:bg-[#EB1C37]/90" : ""}
                    >
                      Yes
                    </Button>
                    <Button
                      type="button"
                      variant={eyesightExaminationPassed === false ? "default" : "outline"}
                      onClick={() => setEyesightExaminationPassed(false)}
                      className={eyesightExaminationPassed === false ? "bg-[#EB1C37] text-white hover:bg-[#EB1C37]/90" : ""}
                    >
                      No
                    </Button>
                  </div>
                </div>

                {/* Eyesight Examination Expiry Date */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Eyesight Examination Expiry Date</Label>
                  <AntdDate
                    name="eyesightExpiryDate"
                    value={eyesightExaminationExpiryDate}
                    onChange={(value: string) => {
                      setEyesightExaminationExpiryDate(value);
                    }}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Qualifications Section */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">Qualifications</Label>
                  <Button
                    type="button"
                    onClick={handleAddQualification}
                    size="sm"
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add Qualification
                  </Button>
                </div>

                <div className="space-y-4 border rounded-lg p-4">
                  {qualifications.map((qualification, index) => (
                    <div key={index} className="grid grid-cols-4 gap-4 p-4 border rounded-lg bg-gray-50">
                      <div className="space-y-2">
                        <Label>Qualification Type <span className="text-red-500">*</span></Label>
                       
                        <div className="relative mt-1">
                          <SearchableSelect
                            value={qualification.qualification_type}
                            onValueChange={(value: string) => {
                              handleQualificationChange(index, 'qualification_type', value);
                            }}
                            placeholder="Select qualification type"
                            searchPlaceholder="Search qualification types..."
                            options={qualificationTypeOptions}
                            onAddNew={() => setIsAddingQualificationType(true)}
                            addNewText="Add New Qualification Type"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Level Type</Label>
                        <Input
                          value={qualification.level_type}
                          onChange={(e) => 
                            handleQualificationChange(index, 'level_type', e.target.value)
                          }
                          placeholder="e.g., CWI, Level 2-AUT-02A/02B (SCR), PCN Level 2D"
                          className="w-full"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Qualification ID</Label>
                        <Input
                          value={qualification.qualification_id || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            handleQualificationChange(index, 'qualification_id', value);
                          }}
                          placeholder="Enter qualification ID"
                          className="w-full"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Expiration Date</Label>
                        <AntdDate
                          name={`qualificationExpiry-${index}`}
                          value={qualification.expiration_date}
                          onChange={(value: string) => 
                            handleQualificationChange(index, 'expiration_date', value)
                          }
                          className="w-full"
                        />
                      </div>

                      {qualifications.length > 1 && (
                        <div className="col-span-4 flex justify-end">
                          <Button
                            type="button"
                            onClick={() => handleRemoveQualification(index)}
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Remove
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* File Upload Section with Certifications/Tags - Only show when adding new team members */}
          {!selectedTeamMember?.id && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[12px] font-medium text-text-grey">Upload Documents
                </Label>
                <p className="text-[12px] text-heritage-grey">
                  Upload documents (PDF, Excel, DOC, etc.) and select tags for each file.
                </p>

                {certificationsLoading ? (
                  <div className="p-3 text-center text-[13px] text-heritage-grey border border-[#E2E5EA] rounded-md">
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-hero-red"></div>
                      <span>Loading certifications...</span>
                    </div>
                  </div>
                ) : (
                  <FileUpload
                    onFileSelect={handleFileSelect}
                    accept=".pdf,.doc,.docx,.xlsx,.xls"
                    multiple={true}
                    maxFiles={100}
                    buttonText="Upload Documents"
                    variant="outline"
                    size="lg"
                    onRemoveFile={handleRemoveFile}
                    selectedFiles={files}
                    showFileList={files.length > 0}
                    className="w-full"
                    maxHeight=""
                    showTags={true}
                    optionsTags={tagOptions}
                    selectedTags={selectedTags}
                    showAddButton={true}
                    addButtonText="Add New Tag"
                    onAddButtonClick={() => setIsAddingTag(true)}
                    onTagsChange={handleTagsChange}
                  />
                )}

                {/* Debug: Show current files count */}
                <div className="text-[11px] text-heritage-grey">
                  Current files: {files.length} file(s)
                </div>
              </div>
            </div>
          )}

          {/* User Permissions Section - Only show when role is selected */}
          {selectedTeamMember?.role && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="permissions" className="text-[12px] font-medium text-text-grey">Permissions</Label>
                <p className="text-[12px] text-heritage-grey">
                  Permissions are automatically configured based on the selected role. You can manually adjust them below if needed.
                </p>
              </div>

              <div className="border border-[#E2E5EA] rounded-lg overflow-visible">
                <div className="overflow-x-auto overflow-y-visible">
                  <table className="w-full text-[13px]">
                    <thead className="bg-page-bg border-b border-[#EEF0F3]">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-text-grey sticky left-0 bg-page-bg z-10 min-w-[200px]">
                          Feature
                        </th>
                        {PERMISSIONS.map((permission) => {
                          const allChecked = allFeaturesHavePermission(permission);

                          return (
                            <th key={permission} className="px-3 py-3 text-center font-semibold text-text-grey relative">
                              <div className="flex flex-col items-center gap-2">
                                <div className="flex items-center gap-1">
                                  <span>{permission}</span>
                                  <Tooltip
                                    content={<p className="max-w-xs text-[11px]">{getPermissionHelp(permission)}</p>}
                                    placement="bottom"
                                    className="inline-flex"
                                  >
                                    <Info className="h-3 w-3 text-heritage-grey cursor-help hover:text-text-grey transition-colors" />
                                  </Tooltip>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Checkbox
                                    checked={allChecked}
                                    onCheckedChange={(checked) => setPermissionForAllFeatures(permission, checked === true)}
                                    className="h-4 w-4"
                                  />
                                  <span className="text-[11px] text-heritage-grey">All</span>
                                </div>
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {FEATURES.map((feature, index) => (
                        <tr key={feature} className={index % 2 === 0 ? 'bg-white' : 'bg-page-bg/50'}>
                          <td className="px-4 py-3 font-medium text-text-grey sticky left-0 bg-inherit z-10">
                            <div className="flex items-center gap-2">
                              <Checkbox
                                checked={PERMISSIONS.every(perm => permissions[feature][perm])}
                                onCheckedChange={(checked) => {
                                  setAllPermissionsForFeature(feature, checked === true);
                                }}
                                className="h-4 w-4"
                              />
                              <span>{getFeatureDisplayName(feature)}</span>
                            </div>
                          </td>
                          {PERMISSIONS.map((permission) => (
                            <td key={permission} className="px-3 py-3 text-center">
                              <Checkbox
                                checked={permissions[feature][permission]}
                                onCheckedChange={() => togglePermission(feature, permission)}
                                className="h-4 w-4"
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>


            </div>
          )}



        </div>

      {/* Add New Tag Dialog */}
      <AddNewItemDialog
        open={isAddingTag}
        onOpenChange={setIsAddingTag}
        title="Add New Tag"
        label="Tag Name"
        placeholder="Enter tag name"
        onAdd={handleAddTag}
        buttonText="Add Tag"
        cancelText="Cancel"
      />

      {/* Add New Qualification Type Dialog */}
      <AddNewItemDialog
        open={isAddingQualificationType}
        onOpenChange={setIsAddingQualificationType}
        title="Add New Qualification Type"
        label="Qualification Type Name"
        placeholder="Enter qualification type (e.g., AUT, Ultrasonic, Magnetic Particle)"
        onAdd={handleAddQualificationType}
        buttonText="Add Qualification Type"
        cancelText="Cancel"
      />
    </div>
  );
}; 